import { GamePageShell } from './GamePageShell';
import type { ReactNode, RefObject } from 'react';
import type { BoxInfo, CardInfo } from '@lub0v/santa-utils';
import type { BoxRoute } from '@src/routing/routes';
import { Stack } from '@src/components';
import { DesktopBoxLayout, MobileBoxLayout, useMobileBoxLayout } from './BoxLayouts';
import { ParticipantCounter } from '../ParticipantCounter';
import { HangingGameSign } from './HangingGameSign';
import { HangingBoxMenu } from './HangingBoxMenu';
import { GameHeader } from './GameHeader';

/** Shared composition for live box pages and the read-only theme preview. */
export function FullBoxFrame({
  info,
  cards,
  route,
  onChanged,
  participantsRef,
  children,
  hideMobileHeading = false,
}: {
  info: BoxInfo;
  cards: CardInfo[];
  route: BoxRoute;
  onChanged: () => Promise<void> | void;
  participantsRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  hideMobileHeading?: boolean;
}) {
  const mobile = useMobileBoxLayout();
  const showPeople = route.view === 'home';
  const hasMembership = Boolean(info.isAdmin || info.isParticipant);
  // Mobile chat fills the viewport and renders this same shell inside its keyboard-aware layout.
  const page = mobile && route.view === 'messenger' ? children : (
    <GamePageShell showHeading={!(mobile && hideMobileHeading)} view={route.view} premium={info.box.isPremium} features={info.box.features}>{children}</GamePageShell>
  );
  if (mobile) {
    return (
      <MobileBoxLayout
        footer={!hasMembership}
        info={info}
        view={showPeople ? 'main' : route.view}
        landscapeForeground={
          showPeople ? (
            <ParticipantCounter
              info={info}
              cards={cards}
              testId="box-participant-counter"
              onClick={() => participantsRef.current?.scrollIntoView({ block: 'start' })}
            />
          ) : undefined
        }
      >
        <Stack gap={6}>
          {!hasMembership && (
            <div className="box-main__overview">
              <section className="box-main__mobile-hero">
                <HangingGameSign info={info} cards={cards} onChanged={onChanged} />
              </section>
            </div>
          )}
          {hasMembership ? (
            page
          ) : (
            <section ref={participantsRef} className="box-main__participants">
              {children}
            </section>
          )}
        </Stack>
      </MobileBoxLayout>
    );
  }
  return (
    <DesktopBoxLayout
      header={
        <GameHeader
          cards={cards}
          info={info}
          onChanged={onChanged}
          main={showPeople}
          navigation={hasMembership ? <HangingBoxMenu info={info} view={route.view} /> : undefined}
        />
      }
    >
      {page}
    </DesktopBoxLayout>
  );
}
